// The history module (#3382 milestone 1): judges every write to a view (journal, loss, alarm).
import { reportUiFailure } from "../../../report-failure.js";
import type { StreamEntry } from "./types.js";
import {
  callerFrames,
  journal,
  lostRows,
  screenRows,
  shapeOf,
  transcriptJournal,
} from "../transcript-journal.js";
import { seqsOf } from "./rows.js";

export const SHRINK_REPORT_INTERVAL_MS = 60_000;

// The judge of every write to a conversation's view (M1 slice 6c): journals it and, when a
// write that drops nothing on purpose took committed rows off the screen, reports it with the
// writer's call path and the journal's tail, the evidence a "my messages vanished" report lacks.
export class WriteJudge {
  // agentId -> the committed rows a cap trimmed inside the write now open.
  private readonly open = new Map<string, Set<number>>();
  // One shrink report per agent per interval: the journal keeps the rest.
  private readonly reportedAt = new Map<string, number>();

  constructor(
    readonly journaled: boolean,
    // What a shrink report says about how the conversation was entered (#3244).
    private readonly context: (agentId: string) => Record<string, string> = () => ({}),
  ) {}

  record(agentId: string, writer: string, fields: Record<string, unknown>): void {
    if (this.journaled) journal(agentId, writer, fields);
  }

  // The committed rows on screen before a write; opens its scope. A scope left open by a
  // write that threw is replaced by the next begin() and never judged (see close()).
  begin(agentId: string, entries: readonly StreamEntry[] | undefined): Set<number> {
    if (!this.journaled || !entries) return new Set();
    this.open.set(agentId, new Set());
    return screenRows(entries, seqsOf);
  }

  // Rows a cap trimmed on purpose: not the loss of the write it runs inside.
  trimmed(agentId: string, rows: ReadonlySet<number>): void {
    const open = this.open.get(agentId);
    if (open) for (const seq of rows) open.add(seq);
  }

  close(agentId: string): void {
    this.open.delete(agentId);
  }

  end(
    agentId: string,
    writer: string,
    before: ReadonlySet<number>,
    entries: readonly StreamEntry[],
    generation: number,
    fields: Record<string, unknown> & { deliberate?: string },
  ): void {
    if (!this.journaled) return;
    const after = screenRows(entries, seqsOf);
    // Trimmed rows are not this write's loss, nor on screen: the shape reads `after` alone.
    const capped = this.open.get(agentId);
    this.open.delete(agentId);
    const accounted = capped ? new Set([...after, ...capped]) : after;
    const lost = lostRows(before, accounted);
    this.record(agentId, writer, {
      ...fields,
      lost,
      capped: capped?.size || undefined,
      after: shapeOf(entries, after),
      generation,
    });
    if (fields.deliberate || lost.count === 0) return;
    const now = Date.now();
    if (now - (this.reportedAt.get(agentId) ?? 0) < SHRINK_REPORT_INTERVAL_MS) return;
    this.reportedAt.set(agentId, now);
    // The counts ride as fields: numbers in the message split one condition into many issues.
    const shrink = new Error(`${writer} took committed rows off the screen`);
    // The writer's call path, not this judge's: the evidence the report exists to carry.
    shrink.stack = callerFrames();
    reportUiFailure("agents.transcript.shrink", shrink, {
      agentId,
      lostRows: lost.count,
      lostFrom: lost.from,
      lostTo: lost.to,
      journal: JSON.stringify(transcriptJournal(agentId).slice(-12)),
      ...this.context(agentId),
    });
  }
}
